import { DEFAULT_PROFILE_DESCRIPTOR } from "building-types/profiles/defaultBoardTrimProfiles";
import type { ProfileExtrusionDescriptor, ProfileTable } from "building-types/profiles/profileTypes";
import { ProfileExtrusionDescriptorType, ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import type {
  ActiveProfileSource,
  ProfileCatalogSettings,
  SettingsToCatalogs,
} from "building-types/settings/profileCatalogSettings";
import type { BaseDescriptor, ViewEntityAttributes } from "building-types/views/baseDescriptorTypes";
import { profileEntitiesToProfileTable, profileEntityToDescriptor } from "doc/utils/profileTools";
import { polyLine2ByTwoPoints, polyLine2ReflectOverYAxis } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";

export function getProfileExtrusionDescriptorProfile(descriptor: ProfileExtrusionDescriptor | undefined): PolyLine2 {
  if (!descriptor) {
    return polyLine2ByTwoPoints(0, 0, 0, 0);
  }
  switch (descriptor.profileType) {
    case ProfileExtrusionDescriptorType.BOARD: {
      return makeBoardProfile(descriptor.height, descriptor.width);
    }
    case ProfileExtrusionDescriptorType.CUSTOM: {
      return descriptor.reflectOverY
        ? polyLine2ReflectOverYAxis(polyLine2New(descriptor.polyline))
        : polyLine2New(descriptor.polyline);
    }
  }
}

export function overrideDescriptorInfo(
  descriptor: BaseDescriptor | null | undefined,
  override: ViewEntityAttributes,
): BaseDescriptor | null {
  if (!descriptor) {
    return null;
  }

  const elevationNote = override.elevationNote || descriptor.elevationNote;
  return { ...descriptor, elevationNoteEnabled: override.elevationNoteEnabled, elevationNote };
}
function makeBoardProfile(height: number, width: number): PolyLine2 {
  const halfHeight = height / 2;
  return polyLine2New([0, 0, 0, halfHeight, width, halfHeight, width, -halfHeight, 0, -halfHeight, 0, 0]);
}

export function getProfileTable(profileSettings: ProfileCatalogSettings): ProfileTable {
  return profileSettings?.profileTable ?? {};
}

export function getProfileDescriptor(
  profileSettings: ProfileCatalogSettings,
  profileId: string,
  fallbackProfile = DEFAULT_PROFILE_DESCRIPTOR,
) {
  return profileSettings.profileTable[profileId] || fallbackProfile;
}

export function getActiveProfileTable({
  settingsToCatalogs,
  profiles,
  profileSettings,
}: ActiveProfileSource): ProfileTable {
  return settingsToCatalogs ? profileEntitiesToProfileTable(profiles ?? {}) : getProfileTable(profileSettings);
}

/** Like `getActiveProfileTable`, but for callers (e.g. settings-only contexts) where a source may not resolve. */
export function getActiveProfileTableOrEmpty(source: ActiveProfileSource | undefined): ProfileTable {
  return source ? getActiveProfileTable(source) : {};
}

//exclude default profile
function getCatalogProfileIds(profileTable: ProfileTable): string[] {
  return Object.keys(profileTable).filter((id) => id !== ProfileExtrusionType.DEFAULT_PROFILE);
}

export function getProfileValues(profileTable: ProfileTable, settingsToCatalogs: SettingsToCatalogs): string[] {
  if (settingsToCatalogs) {
    return Object.keys(profileTable);
  }
  return [ProfileExtrusionType.DEFAULT_PROFILE, ...getCatalogProfileIds(profileTable)];
}

export function getProfileValuesWithNone(profileTable: ProfileTable, settingsToCatalogs: SettingsToCatalogs): string[] {
  return [ProfileExtrusionType.NONE, ...getProfileValues(profileTable, settingsToCatalogs)];
}

export function getActiveProfileDescriptorOrUndefined(
  source: ActiveProfileSource,
  profileId: string,
): ProfileExtrusionDescriptor | undefined {
  if (source.settingsToCatalogs) {
    const profile = source.profiles?.[profileId];
    return profile ? profileEntityToDescriptor(profile) : undefined;
  }
  return source.profileSettings?.profileTable[profileId];
}

/**
 * Resolves a profile id to its descriptor. In the legacy (flag-off) world this always succeeds,
 * falling back to `legacyFallbackProfile` when the id isn't in the settings' profileTable. In the
 * flag-on world there is no fallback: a missing PROFILE entity resolves to `undefined` so callers
 * can report it via `pushMissingProfileReferenceIssue` instead of silently substituting a default.
 */
export function getActiveProfileDescriptor(
  source: ActiveProfileSource,
  profileId: string,
  legacyFallbackProfile = DEFAULT_PROFILE_DESCRIPTOR,
): ProfileExtrusionDescriptor | undefined {
  if (source.settingsToCatalogs) {
    return getActiveProfileDescriptorOrUndefined(source, profileId);
  }
  return getProfileDescriptor(source.profileSettings, profileId, legacyFallbackProfile);
}
